import React, { useEffect, useRef, useState } from "react";

/**
 * Horizontal wrapper for data tables.
 *
 * Wide financial tables cannot fit a phone, so they scroll sideways. This adds the
 * two things that were missing: an edge fade that shows when more columns are
 * hidden, and a one-off "swipe" hint that disappears once you have scrolled. The
 * region is focusable so keyboard users can reach the hidden columns with the
 * arrow keys.
 */
const TableScroller = ({
  children,
  className = "",
  hint = true,
  label = "Table",
  scrollerRef: externalRef,
}) => {
  const scrollerRef = useRef(null);
  const [state, setState] = useState({
    overflow: false,
    atStart: true,
    atEnd: false,
    scrolled: false,
  });

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return undefined;

    const measure = () => {
      const overflow = el.scrollWidth > el.clientWidth + 2;
      setState((prev) => ({
        overflow,
        atStart: el.scrollLeft <= 2,
        atEnd: el.scrollLeft + el.clientWidth >= el.scrollWidth - 2,
        scrolled: prev.scrolled || el.scrollLeft > 8,
      }));
    };

    measure();
    el.addEventListener("scroll", measure, { passive: true });
    window.addEventListener("resize", measure);

    let observer;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(measure);
      observer.observe(el);
    }

    return () => {
      el.removeEventListener("scroll", measure);
      window.removeEventListener("resize", measure);
      if (observer) observer.disconnect();
    };
  }, []);

  const classes = [
    "table-scroller-wrap",
    state.overflow ? "is-overflowing" : "",
    state.atStart ? "" : "is-scrolled-start",
    state.atEnd ? "is-at-end" : "",
    state.scrolled ? "has-scrolled" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={classes}>
      <div
        ref={(node) => {
          scrollerRef.current = node;
          if (externalRef) externalRef.current = node;
        }}
        className={`table-scroller ${className}`.trim()}
        tabIndex={0}
        role="region"
        aria-label={`${label}, scrolls sideways for more columns`}
      >
        {children}
      </div>

      {hint && state.overflow && !state.scrolled && (
        <p className="table-scroll-hint" aria-hidden="true">
          <span className="d-md-none">Swipe sideways to see all columns</span>
          <span className="d-none d-md-inline">
            Scroll sideways to see all columns
          </span>
        </p>
      )}
    </div>
  );
};

export default TableScroller;
